<template>
    <TDataTable
        :value="formattedExpensesList"
        class="expense-table"
        data-testid="expense-list-table"
        @row-click="
            (row: any) =>
                navigateTo({
                    name: 'project-projectId-expense-validation-expenseId',
                    params: {
                        projectId,
                        expenseId: row.data.id,
                    },
                })
        "
    >
        <TColumn
            v-for="column in ['type', 'total', 'date'] as ExpenseTableColumn[]"
            :key="column"
            :field="column"
            class="expense-table__column large-size"
            :class="`${column}-column`"
        >
            <template #header>
                <p class="joy-font-primary-350">
                    {{ t(`projectclient.expenses.validation.table.column.${column}`) }}
                </p>
            </template>
            <template #body="{data}">
                <div v-if="column === 'type'" class="category / flex">
                    <div class="category__supplier-info / flex">
                        <VJoyIcon class="category__supplier-info__icon" :name="categoriesIcons[data.type as ExpenseCategory]" size="small" />
                        <div class="flex flex-col">
                            <span class="joy-font-primary-350">{{ categoriesLabels[data.type as ExpenseCategory] }}</span>
                            <span v-if="data.type !== 'MILEAGE_ALLOWANCE'">{{ data.supplier }}</span>
                        </div>
                    </div>
                    <VJoyTooltip v-if="data.description" class="category__additional-info" :text="data.description" variant="secondary">
                        <VJoyIcon class="category__additional-info__icon" name="info-circle" size="xsmall" />
                    </VJoyTooltip>
                </div>
                <div v-else>{{ data[column] }}</div>
            </template>
        </TColumn>
    </TDataTable>
</template>

<script setup lang="ts">
    import {useExpenseCategoryMapper} from '@common/utils';
    import {storeToRefs} from 'pinia';
    import {navigateTo, useRoute, useTranslation} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {VJoyIcon, VJoyTooltip} from '@maltjoy/core-vue';
    import {ExpenseCategory} from '@api';
    import {useExpenseValidationStore} from '../stores';
    import type {ExpenseTableColumn, FormattedExpenseTableItem} from '../types';

    const {t} = useTranslation();

    const route = useRoute('project-projectId-expense-validation');
    const projectId = route.params.projectId;

    // expenseValidation store
    const expenseValidationStore = useExpenseValidationStore();
    const {formattedExpensesList} = storeToRefs(expenseValidationStore);

    // table structure
    const {TDataTable, TColumn} = createDataTableComponents<FormattedExpenseTableItem>();

    const {categoriesLabels, categoriesIcons} = useExpenseCategoryMapper();
</script>

<style lang="scss" scoped>
    .expense-table {
        overflow: auto;
        border: 1px solid var(--joy-color-neutral-30) !important;
        border-radius: var(--joy-core-radius-3) !important;

        :deep(th.expense-table__column),
        :deep(td.expense-table__column) {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            padding: 0 var(--joy-core-spacing-3);

            &:hover {
                cursor: pointer;
            }

            &.total-column,
            &.date-column {
                text-align: right;

                .p-datatable-column-header-content {
                    justify-content: flex-end;
                }
            }

            &.large-size {
                height: 56px;
            }
        }
    }

    .category {
        justify-content: space-between;
        flex-wrap: nowrap;

        &__supplier-info {
            align-items: center;
            flex-wrap: nowrap;

            &__icon {
                color: 1px solid var(--joy-color-neutral-30);
                margin-right: var(--joy-core-spacing-2);
            }
        }

        &__additional-info {
            display: flex;
            align-items: center;
            margin-left: var(--joy-core-spacing-2);

            // needed to center icon in within tooltip
            :deep(.joy-tooltip__wrapper) {
                display: flex;
            }

            &__icon {
                color: 1px solid var(--joy-color-neutral-30);
            }
        }
    }
</style>
